import { Button, DialogActions, Skeleton, Typography } from '@mui/material';
import { DialogWithCloseButton } from 'components/atoms/Dialog/DialogWithCloseButton';
import { RunBrightfieldTissueSegmentationMaskJob } from 'components/StudyDashboard/ProceduresPage/Actions/Brightfield/RunBrightfieldTissueSegmentationMaskJob';
import { ExecuteStudyAlgorithm } from 'components/StudyDashboard/ProceduresPage/Actions/ExecuteStudyAlgorithms';
import { GenerateStudyReport } from 'components/StudyDashboard/ProceduresPage/Actions/GenerateStudyReport';
import { RunCellSegmentation } from 'components/StudyDashboard/ProceduresPage/Actions/InferenceDependentModels/RunCellSegmentation';
import { RunDetectTmaCores } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunDetectTmaCores';
import { RunManualPostIngestPipeline } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunManualPostIngestPipeline';
import { RunMifMarkerUnsupervisedAnalysis } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMifMarkerUnsupervisedAnalysis';
import { RunMultiplexBinaryClassifier } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexBinaryClassifier';
import { RunMultiplexBlobSegmentation } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexBlobSegmentation';
import { RunMultiplexCellSegmentation } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexCellSegmentation';
import { RunMultiplexCellTyping } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexCellTyping';
import { RunMultiplexHistogram } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexHistogram';
import { RunMultiplexNormalization } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexNormalization';
import { RunMultiplexPeakDetection } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexPeakDetection';
import { RunMultiplexThreshold } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexThreshold';
import { RunMultiplexTissueSegmentationMask } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexTissueSegmentationMask';
import { RunQaAnalysis } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunQaAnalysis';
import { RunQcAnalysis } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunQcAnalysis';
import { PostProcessingAndCalculateFeatures } from 'components/StudyDashboard/ProceduresPage/Actions/PostProcessingAndCalculateFeatures';
import { RebuildSecondaryAnalysisAction } from 'components/StudyDashboard/ProceduresPage/Actions/PostProcessingAndCalculateFeatures/RebuildSecondaryAnalysisAction';
import PrepareCustomerResultsJob from 'components/StudyDashboard/ProceduresPage/Actions/PrepareCustomerResultsForDownload';
import { RunCloudOptimizedSlidesJob } from 'components/StudyDashboard/ProceduresPage/Actions/RunCloudOptimizedSlidesJob';
import RunCropTma from 'components/StudyDashboard/ProceduresPage/Actions/RunCropTma';
import { RunInferenceSteps } from 'components/StudyDashboard/ProceduresPage/Actions/RunInference/RunInferenceSteps';
import { RunNeighborhoodClusters } from 'components/StudyDashboard/ProceduresPage/Actions/RunNeighborhoodClusters';
import { RunQuantification } from 'components/StudyDashboard/ProceduresPage/Actions/RunQuantification';
import { RunSlideRegistrations } from 'components/StudyDashboard/ProceduresPage/Actions/RunSlideRegistrations';
import { RunTileGenerationJob } from 'components/StudyDashboard/ProceduresPage/Actions/RunTileGenerationJob';
import { JobType } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { includes } from 'lodash';
import React from 'react';
import { CasesParams } from 'utils/useCasesParams/types';
import { usePermissions } from 'utils/usePermissions';
import useStudy from 'utils/useStudy';
import { useStudyPlatformVersionGate } from './useStudyPlatformVersionGate';

interface Props {
  jobType: JobType;
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
  jobId?: string;
  additionalParams?: Record<string, any>;
}

interface InferenceDialogAdditionalParams {
  inferenceDialogHeader?: string;
  inferencePresetModelType?: string;
}

const jobsWithoutDockerImageCheck = [JobType.PrepareCustomerResults, JobType.TileGeneration];

const JobModal: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  jobType,
  onClose,
  casesParams,
  jobId,
  additionalParams,
}) => {
  const { hasPermission } = usePermissions();
  const canRunInference = hasPermission(Permission.RunInference);
  const canRunSlideRegistrations = hasPermission(Permission.ExecuteSlideRegistrations);
  const canRunMultiplexCellSegmentation = hasPermission(Permission.RunMultiplexCellSegmentation);
  const canRunMultiplexBinaryClassifier = hasPermission(Permission.ExecuteMultiplexBinaryClassifier);
  const canRunMultiplexTissueSegmentationMask = hasPermission(Permission.ExecuteMultiplexTissueSegmentationMask);
  const canRunManualPostIngestPipeline = hasPermission(Permission.ExecuteManualPostIngestPipeline);
  const canRunBrightfieldTissueSegmentationMaskJob = hasPermission(Permission.ExecuteBrightfieldTissueSegmentationMask);
  const canRunMultiplexNormalization = hasPermission(Permission.ExecuteMultiplexNormalization);
  const canRunMultiplexHistogram = hasPermission(Permission.ExecuteMultiplexHistogram);
  const canRunMultiplexThreshold = hasPermission(Permission.ExecuteMultiplexThreshold);
  const canRunMultiplexCellTyping = hasPermission(Permission.ExecuteMultiplexCellTyping);
  const canRunMultiplexBlobSegmentation = hasPermission(Permission.ExecuteMultiplexBlobSegmentation);
  const canRunMultiplexPeakDetection = hasPermission(Permission.ExecuteMultiplexPeakDetection);
  const canExecuteStudyAlgorithms = hasPermission(Permission.ExecuteStudyAlgorithms);
  const canExecuteTileGeneration = hasPermission(Permission.ExecuteTileGeneration);
  const canExecuteNeighborClusters = hasPermission(Permission.ExecuteNeighborhoodClusters);
  const canExecuteCellSegmentation = hasPermission(Permission.ExecuteCellSegmentation);
  const canExecuteQuantification = hasPermission(Permission.ExecuteQuantification);
  const canRunCropTma = hasPermission(Permission.RunCropTma);
  const canRunCalculateFeatures = hasPermission(Permission.RunCalculateFeatures);
  const canRunSecondaryAnalysis = hasPermission(Permission.RunSecondaryAnalysis);
  const canExecuteQaAnalysis = hasPermission(Permission.ExecuteQaAnalysis);
  const canExecuteQcAnalysis = hasPermission(Permission.ExecuteQcAnalysis);
  const canExecuteMifMarkerUnsupervisedAnalysis = hasPermission(Permission.ExecuteMifMarkerUnsupervisedAnalysis);
  const canExecuteStudyReport = hasPermission(Permission.ExecuteStudyReport);
  const canPrepareApprovedCustomerResultsForDownload = hasPermission(
    Permission.PrepareApprovedCustomerResultsForDownload
  );

  const studyId = casesParams?.filters?.studyId || casesParams?.studyId;
  const inferenceDialogAdditionalParams = additionalParams as InferenceDialogAdditionalParams | undefined;

  const { study, isLoading } = useStudy(studyId);
  const { isLoading: isLoadingPlatformVersion, platformVersionWarning } = useStudyPlatformVersionGate(studyId, {
    enabled: !includes(jobsWithoutDockerImageCheck, jobType),
  });

  const renderDialog = (
    header: string,
    content: React.ReactNode,
    dialogProps?: Partial<React.ComponentProps<typeof DialogWithCloseButton>>
  ) => (
    <DialogWithCloseButton header={header} onClose={onClose} {...dialogProps}>
      <>
        {platformVersionWarning}
        {content}
      </>
    </DialogWithCloseButton>
  );

  if (!studyId) {
    // This should not happen, but just in case
    console.error('JobModal: studyId is undefined', { casesParams });
    return (
      <DialogWithCloseButton header="No Study Selected" onClose={() => onClose(undefined, true)}>
        <Typography>Please select a study to run this job.</Typography>
        <DialogActions>
          <Button onClick={() => onClose(undefined, true)} color="primary" autoFocus>
            Close
          </Button>
        </DialogActions>
      </DialogWithCloseButton>
    );
  } else if (isLoading || isLoadingPlatformVersion) {
    return (
      <DialogWithCloseButton header="Loading..." onClose={() => onClose(undefined, true)}>
        <Skeleton variant="rectangular" width="100%" height={600} />
      </DialogWithCloseButton>
    );
  }

  switch (jobType) {
    case JobType.PostProcessingAndCalculateFeatures:
      if (additionalParams?.isSecondaryAnalysisRebuild) {
        if (canRunSecondaryAnalysis) {
          return (
            <RebuildSecondaryAnalysisAction
              jobId={jobId}
              casesParams={casesParams}
              onClose={onClose}
              platformVersionWarning={platformVersionWarning}
            />
          );
        } else {
          onClose(undefined, true);
          return null;
        }
      }

      if (canRunCalculateFeatures) {
        return (
          <PostProcessingAndCalculateFeatures
            jobId={jobId}
            casesParams={casesParams}
            onClose={onClose}
            additionalParams={additionalParams}
            platformVersionWarning={platformVersionWarning}
          />
        );
      } else {
        console.warn('User does not have permission to run calculate features');
      }
      break;
    case JobType.Inference:
      if (canRunInference) {
        return renderDialog(
          inferenceDialogAdditionalParams?.inferenceDialogHeader || 'Run Inference',
          <RunInferenceSteps
            jobId={jobId}
            casesParams={casesParams}
            onClose={onClose}
            presetModelType={inferenceDialogAdditionalParams?.inferencePresetModelType}
          />
        );
      }
      break;
    case JobType.MultiplexCellSegmentation:
      if (canRunMultiplexCellSegmentation) {
        return renderDialog('Run Multiplex Cell Segmentation', <RunMultiplexCellSegmentation onClose={onClose} />);
      }
      break;
    case JobType.BinaryClassifier:
      if (canRunMultiplexBinaryClassifier) {
        return renderDialog('Run Multiplex Binary Classifier', <RunMultiplexBinaryClassifier onClose={onClose} />);
      }
      break;
    case JobType.MultiplexTissueSegmentationMask:
      if (canRunMultiplexTissueSegmentationMask) {
        return renderDialog(
          'Run Multiplex Tissue Segmentation Mask',
          <RunMultiplexTissueSegmentationMask onClose={onClose} />
        );
      }
      break;
    case JobType.ManualPostIngestPipeline:
      if (canRunManualPostIngestPipeline) {
        return renderDialog(
          'Run initial image analysis',
          <RunManualPostIngestPipeline onClose={onClose} casesParams={casesParams} study={study} />
        );
      }
      break;
    case JobType.BrightfieldTissueSegmentationMask:
      if (canRunBrightfieldTissueSegmentationMaskJob) {
        return renderDialog(
          'Run Brightfield Tissue Segmentation Mask',
          <RunBrightfieldTissueSegmentationMaskJob onClose={onClose} />
        );
      }
      break;
    case JobType.CloudOptimizedSlides:
      if (canExecuteStudyAlgorithms) {
        return (
          <RunCloudOptimizedSlidesJob
            onClose={onClose}
            casesParams={casesParams}
            platformVersionWarning={platformVersionWarning}
          />
        );
      }
      break;
    case JobType.TileGeneration:
      if (canExecuteTileGeneration) {
        return renderDialog(
          'Run Tile Generation',
          <RunTileGenerationJob jobId={jobId} onClose={onClose} casesParams={casesParams} />
        );
      }
      break;
    case JobType.MultiplexHistogram:
      if (canRunMultiplexHistogram) {
        return renderDialog(
          'Run Multiplex Histogram',
          <RunMultiplexHistogram jobId={jobId} casesParams={casesParams} onClose={onClose} />
        );
      }
      break;
    case JobType.MultiplexNormalization:
      if (canRunMultiplexNormalization) {
        return renderDialog(
          'Run Multiplex Normalization',
          <RunMultiplexNormalization jobId={jobId} casesParams={casesParams} onClose={onClose} />
        );
      }
      break;
    case JobType.MultiplexThreshold:
      if (canRunMultiplexThreshold) {
        return renderDialog('Run Multiplex Threshold', <RunMultiplexThreshold onClose={onClose} />);
      }
      break;
    case JobType.MultiplexCellTyping:
      if (canRunMultiplexCellTyping) {
        return renderDialog('Fit KNN Model For Multiplex Cell Typing', <RunMultiplexCellTyping onClose={onClose} />);
      }
      break;
    case JobType.MultiplexPeakDetection:
      if (canRunMultiplexPeakDetection) {
        return (
          <RunMultiplexPeakDetection
            onClose={onClose}
            casesParams={casesParams}
            platformVersionWarning={platformVersionWarning}
          />
        );
      }
      break;
    case JobType.MultiplexBlobSegmentation:
      if (canRunMultiplexBlobSegmentation) {
        return renderDialog('Run Blob Segmentation', <RunMultiplexBlobSegmentation onClose={onClose} />);
      }
      break;
    case JobType.SlideRegistrations:
      if (canRunSlideRegistrations) {
        return renderDialog(
          'Run Slide Registrations',
          <RunSlideRegistrations onClose={onClose} casesParams={casesParams} />
        );
      }
      break;
    case JobType.NucleaiExecutor:
      if (canExecuteStudyAlgorithms) {
        return renderDialog(
          'Run Study Algorithm',
          <ExecuteStudyAlgorithm onClose={onClose} casesParams={casesParams} />
        );
      }
      break;
    case JobType.NeighborhoodClusters:
      if (canExecuteNeighborClusters) {
        return renderDialog(
          'Run Neighborhood Clusters',
          <RunNeighborhoodClusters onClose={onClose} casesParams={casesParams} />
        );
      }
      break;
    case JobType.CropTma:
      if (canRunCropTma) {
        return (
          <RunCropTma onClose={onClose} casesParams={casesParams} platformVersionWarning={platformVersionWarning} />
        );
      }
      break;
    case JobType.DetectTmaCores:
      if (canRunCropTma) {
        return renderDialog('Detect TMA wells', <RunDetectTmaCores onClose={onClose} casesParams={casesParams} />);
      }
      break;
    case JobType.CellSegmentation:
      if (canExecuteCellSegmentation) {
        return renderDialog(
          'Run Cell Segmentation',
          <RunCellSegmentation onClose={onClose} casesParams={casesParams} />
        );
      }
      break;
    case JobType.CellQuantification:
      if (canExecuteQuantification) {
        return renderDialog('Run Quantification', <RunQuantification onClose={onClose} casesParams={casesParams} />);
      }
      break;
    case JobType.QaAnalysis:
      if (canExecuteQaAnalysis) {
        return renderDialog('Run QA Analysis', <RunQaAnalysis onClose={onClose} casesParams={casesParams} />);
      }
      break;
    case JobType.QcAnalysis:
      if (canExecuteQcAnalysis) {
        return renderDialog('Run QC Analysis', <RunQcAnalysis onClose={onClose} casesParams={casesParams} />);
      }
      break;
    case JobType.MifMarkerUnsupervisedAnalysis:
      if (canExecuteMifMarkerUnsupervisedAnalysis) {
        return renderDialog(
          'Run mIF Marker Unsupervised Analysis',
          <RunMifMarkerUnsupervisedAnalysis onClose={onClose} casesParams={casesParams} />
        );
      }
      break;
    case JobType.StudyReport:
      if (canExecuteStudyReport) {
        return renderDialog(
          'Generate Study Report',
          <GenerateStudyReport onClose={onClose} casesParams={casesParams} />
        );
      }
      break;

    case JobType.PrepareCustomerResults:
      if (canPrepareApprovedCustomerResultsForDownload) {
        return renderDialog(
          'Prepare Customer Results',
          <PrepareCustomerResultsJob onClose={onClose} casesParams={casesParams} />
        );
      }
      break;

    default:
      console.error(`JobModal: jobType ${jobType} not found`);
      return null;
  }
  console.error(`User does not have permission to run job type ${jobType}`);
  return null;
};

export default JobModal;
